import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as Popover from "@radix-ui/react-popover";
import { debounce } from "lodash";

import { useCurrentFullscreenElement } from "~/common/Fullscreen";
import { ControlTooltip, FellowVideoPlayerButton } from "~/meetingRecorder/Video";

import styles from "./PlaybackRateSelector.module.css";

interface PlaybackRateSelectorProps {
	videoRef: React.RefObject<HTMLVideoElement>;
	onPlaybackRateChange(playbackRate: number): void;
}

// Constants for the slider
const MIN_SPEED = 0.75;
const MAX_SPEED = 2.5;
const STEP = 0.05;
// Default quick option steps shown below the slider
const DEFAULT_STEPS: number[] = [1.0, 1.2, 1.5, 1.7, 2.0];

export default function PlaybackRateSelector({ videoRef, onPlaybackRateChange }: PlaybackRateSelectorProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [currentSpeed, setCurrentSpeed] = useState(videoRef.current?.playbackRate ?? 1);

	// Actual video playback rate for the trigger display
	const actualVideoSpeed = videoRef.current?.playbackRate ?? 1;
	const currentFullscreenElement = useCurrentFullscreenElement();

	const sliderRef = useRef<HTMLInputElement>(null);

	// Find the portal container for the video player. This is needed to mount the popover
	// in the correct location when viewing a recap via the `TranscriptModal`
	const portalContainer = useMemo(() => {
		const video = videoRef.current;
		if (!video) return null;
		return video.closest("div[data-video-portal-container='true']");
	}, [videoRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

	// Debounced video update function with both leading and trailing edges
	const debouncedVideoUpdate = useMemo(
		() => debounce(onPlaybackRateChange, 200, { leading: true, trailing: true }),
		[onPlaybackRateChange],
	);

	const formatSpeed = (speed: number) => `${speed}x`;
	const formatPreset = (speed: number) => `${speed.toFixed(1)}`;

	// Calculate slider fill percentage and thumb position
	const fillPercentage = ((currentSpeed - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)) * 100;

	const handleSliderChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = parseFloat(event.target.value);
			setCurrentSpeed(value); // Immediate visual update
			debouncedVideoUpdate(value); // Debounced video update
		},
		[debouncedVideoUpdate],
	);

	const handleSpeedAdjust = useCallback(
		(delta: number) => {
			const newSpeed = Math.max(MIN_SPEED, Math.min(MAX_SPEED, currentSpeed + delta));
			const finalSpeed = Number(newSpeed.toFixed(2));
			setCurrentSpeed(finalSpeed); // Immediate visual update
			onPlaybackRateChange(finalSpeed); // Immediate update for button clicks
		},
		[currentSpeed, onPlaybackRateChange],
	);

	// Quick option click
	const handleQuickOptionClick = useCallback(
		(speed: number) => {
			const bounded = Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed));
			setCurrentSpeed(bounded);
			onPlaybackRateChange(bounded);
			setIsOpen(false); // Close popover after selecting preset
		},
		[onPlaybackRateChange],
	);

	// Sync with video element on mount and when popover opens
	useEffect(() => {
		if (isOpen && videoRef.current) {
			setCurrentSpeed(videoRef.current.playbackRate);
		}
	}, [isOpen, videoRef]);

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => debouncedVideoUpdate.cancel?.();
	}, [debouncedVideoUpdate]);

	// Dynamic styles for the slider container
	const sliderContainerStyle = {
		"--fill-percentage": `${fillPercentage}%`,
		"--thumb-position": fillPercentage,
	} as React.CSSProperties;

	return (
		<FellowVideoPlayerButton>
			<span slot="icon">
				<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
					<Popover.Trigger className={styles.playbackRateTrigger}>
						<ControlTooltip content="Playback speed">
							<span>{formatSpeed(actualVideoSpeed)}</span>
						</ControlTooltip>
					</Popover.Trigger>
					<Popover.Portal container={currentFullscreenElement ?? portalContainer ?? undefined}>
						<Popover.Content side="top" sideOffset={8} className={styles.playbackRateSelector}>
							<div className={styles.playbackSliderContainer} style={sliderContainerStyle}>
								<div className={styles.sliderWrapper}>
									{/* Track background with embedded progress for clean clipping */}
									<div className={styles.sliderBackground}>
										<div className={styles.sliderProgress} style={{ width: `${fillPercentage}%` }} />
									</div>

									{/* Single 1x tick (bar) */}
									<div className={styles.sliderTicks}>
										{(() => {
											const position = ((1.0 - MIN_SPEED) / (MAX_SPEED - MIN_SPEED)) * 100;
											return <div className={styles.oneXTick} style={{ left: `${position}%` }} />;
										})()}
									</div>

									{/* Slider input on top */}
									<input
										ref={sliderRef}
										type="range"
										min={MIN_SPEED}
										max={MAX_SPEED}
										step={STEP}
										value={currentSpeed}
										onChange={handleSliderChange}
										className={styles.speedSlider}
										aria-label="Playback speed"
										aria-valuetext={formatSpeed(currentSpeed)}
									/>
									{/* Current value inside slider thumb */}
									<div className={styles.currentValueDisplay}>
										<span className={styles.currentValue}>{formatSpeed(currentSpeed)}</span>
									</div>
								</div>

								{/* Footer with - quick options + */}
								<div className={styles.sliderFooter}>
									<button
										className={styles.speedAdjustButton}
										onClick={() => handleSpeedAdjust(-STEP)}
										disabled={currentSpeed <= MIN_SPEED}
										title="Decrease speed"
									>
										−
									</button>

									<div className={styles.quickOptions}>
										{DEFAULT_STEPS.map(speed => (
											<button
												key={speed}
												className={
													Math.abs(currentSpeed - speed) < 0.01
														? `${styles.quickOptionButton} ${styles.active}`
														: styles.quickOptionButton
												}
												onClick={() => handleQuickOptionClick(speed)}
											>
												{formatPreset(speed)}
											</button>
										))}
									</div>

									<button
										className={styles.speedAdjustButton}
										onClick={() => handleSpeedAdjust(STEP)}
										disabled={currentSpeed >= MAX_SPEED}
										title="Increase speed"
									>
										+
									</button>
								</div>
							</div>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			</span>
		</FellowVideoPlayerButton>
	);
}
